import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import ErrorMessage from '../../ErrorMessage';
import { useLoginContext } from '../LoginContext';
import { CopyIcon, EyeIcon, EyeOffIcon, ShieldCheckIcon } from '../../../assets/icons';

const INPUT_IDS = ['mfa-0', 'mfa-1', 'mfa-2', 'mfa-3', 'mfa-4', 'mfa-5'];

function Step2({ authenticateSubmit }) {
  const { userCode, setUserCode, qrCodeImage, secretCode, error } = useLoginContext();
  const [copied, setCopied] = useState(false);
  const [showSecretCode, setShowSecretCode] = useState(false);
  const [code, setCode] = useState(() => (typeof userCode === 'string' && userCode.length === 6 ? userCode.split('') : ['', '', '', '', '', '']));
  const inputRefs = useRef([]);
  const formRef = useRef(null);

  useEffect(() => {
    if (typeof userCode === 'string' && userCode.length === 6 && /^\d{6}$/.test(userCode)) {
      setCode(userCode.split(''));
    }
  }, [userCode]);

  useEffect(() => {
    setUserCode(code.join(''));
  }, [code, setUserCode]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleCodeChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pastedData)) {
      setCode(pastedData.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (code.join('').length === 6) {
        formRef.current?.requestSubmit();
      }
      return;
    }
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleCopySecretCode = () => {
    navigator.clipboard.writeText(secretCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const codeString = code.join('');
    setUserCode(codeString);
    setTimeout(() => authenticateSubmit(e), 0);
  };

  return (
    <div className='w-full overflow-hidden rounded-[20px] bg-white px-6 py-9 shadow-[0_4px_32px_rgba(11,25,41,0.08)] sm:px-12 sm:py-11'>
      <div className='text-center'>
        <div className='mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#EBF4FB] text-[#0F2540]'>
          <ShieldCheckIcon className='h-7 w-7' strokeWidth='1.75' />
        </div>
        <h5 className='font-serif text-2xl font-normal leading-tight text-[#0B1929]'>Set up Two-Factor Authentication</h5>
        <p className='mt-2 text-sm leading-6 text-[#7A9AB0]'>
          Scan this QR code with a security or authentication app like <span className='text-primary'>Google Authenticator</span> or{' '}
          <span className='text-primary'>Microsoft Authenticator</span>.
        </p>
      </div>

      <div className='mt-7 flex flex-col items-center gap-5'>
        <div className='rounded-[16px] border-[1.5px] border-[#DDE6EF] bg-white p-4 shadow-[0_2px_16px_rgba(11,25,41,0.06)]'>
          <img src={qrCodeImage} alt='QR Code' className='h-48 w-48' />
        </div>

        {secretCode && (
          <div className='w-full'>
            <p className='mb-3 text-center text-sm leading-6 text-[#7A9AB0]'>
              If you&apos;re unable to scan the QR code, please enter the code provided below into your authentication app.
            </p>
            <div className='flex items-center gap-2 rounded-[10px] border-[1.5px] border-[#C4DDEF] bg-[#EBF4FB] p-3'>
              {showSecretCode ? (
                <>
                  <code className='flex-1 break-all font-mono text-sm text-[#3D5A73]'>{secretCode}</code>
                  <button
                    type='button'
                    onClick={handleCopySecretCode}
                    className='flex-shrink-0 rounded-md p-1.5 text-[#2E6BAD] transition hover:bg-white/70'
                    title='Copy to clipboard'
                  >
                    {copied ? <span className='text-xs font-medium text-[#1A7A4A]'>Copied!</span> : <CopyIcon className='h-4 w-4' />}
                  </button>
                  <button
                    type='button'
                    onClick={() => setShowSecretCode(false)}
                    className='flex-shrink-0 rounded-md p-1.5 text-[#2E6BAD] transition hover:bg-white/70'
                    title='Hide code'
                  >
                    <EyeOffIcon className='h-4 w-4' />
                  </button>
                </>
              ) : (
                <button
                  type='button'
                  onClick={() => setShowSecretCode(true)}
                  className='flex flex-1 items-center justify-center gap-2 py-1 text-sm font-medium text-[#2E6BAD] transition hover:text-[#1E4E80]'
                >
                  <EyeIcon className='h-4 w-4' />
                  Click to show code
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <form ref={formRef} onSubmit={handleSubmit} className='mt-7 space-y-4'>
        <div className='space-y-3'>
          <label htmlFor={INPUT_IDS[0]} className='block text-center text-[11.5px] font-semibold text-[#7A9AB0]'>
            Please enter your one-time password
          </label>
          <div className='flex flex-col items-center gap-3 sm:flex-row'>
            <div className='flex items-center justify-center gap-2'>
              {code.map((digit, index) => (
                <input
                  key={INPUT_IDS[index]}
                  id={INPUT_IDS[index]}
                  ref={(el) => {
                    if (el) inputRefs.current[index] = el;
                  }}
                  type='text'
                  inputMode='numeric'
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className='flex h-12 w-11 items-center justify-center rounded-[10px] border-[1.5px] border-[#DDE6EF] bg-white text-center text-lg font-semibold text-[#0B1929] outline-none transition focus:border-[#2E6BAD] focus:ring-4 focus:ring-[#2E6BAD]/10 disabled:cursor-not-allowed disabled:opacity-50'
                />
              ))}
            </div>
            <button
              type='submit'
              disabled={!qrCodeImage || code.join('').length !== 6}
              className='inline-flex h-12 w-full items-center justify-center rounded-full bg-[#0B1929] px-7 text-sm font-semibold text-white transition hover:bg-[#0F2540] disabled:cursor-not-allowed disabled:bg-[#B8C9D9] sm:w-auto'
            >
              Continue
            </button>
          </div>
        </div>
        {error && <ErrorMessage error={error} />}
      </form>
    </div>
  );
}

Step2.propTypes = {
  authenticateSubmit: PropTypes.func.isRequired,
};

export default Step2;
